<script>
import { mapState } from 'vuex';
import AdditionalVisitorsService from '@/services/AdditionalVisitorsService';
import WarningModal from '@/components/modals/WarningModal.vue';
import moment from 'moment';
import InCellVisitsService from '@/services/InCellVisitsService';

export default {
  components: {
    WarningModal,
  },
  props: {},
  data() {
    return {
      headers: [
        { key: 'fullName', label: 'Name' },
        { key: 'birthDateAndAge', label: 'Date of Birth' },
        { key: 'status', label: 'Verification status' },
        { key: 'add', label: '', thStyle: { width: '5%' } },
      ],
      activeParticipant: null,
      loading: false,
      extraParticipants: [],
      currentPage: 1,
    };
  },
  computed: {
    ...mapState({
      visit: state => state.inCellVisits.visit,
      additionalVisitors: state => state.inCellVisits.additionalVisitors,
    }),
  },
  mounted() {
    this.getParticipants();
  },
  methods: {
    showModal(name, value, person = null) {
      this.activeParticipant = person;
      if (value) {
        this.$bvModal.show(name);
      } else {
        this.$bvModal.hide(name);
      }
    },
    async closeAllModalAndRefresh() {
      this.$bvModal.hide('modal-add-participant');
      await this.$store.dispatch('inCellVisits/getAdditionalVisitors', this.visit._id);
      this.getVisit();
      this.getParticipants();
    },
    getVisit() {
      this.loading = true;
      InCellVisitsService.getVisit(this.visit._id).then(response => {
        this.$store.dispatch('inCellVisits/setVisit', response.data);
      }).catch(e => {
        console.error('Error getting in-cell call: ', e);
        this.$toast.error('Error getting in-room call', {
          toastClassName: ['toast-std', 'warning-toast'],
        });
      }).finally(() => {
        this.loading = false;
      });
    },
    getParticipants() {
      this.loading = true;
      AdditionalVisitorsService.getAdditionalVisitors(this.visit.visitor.user_id).then(res => {
        const participants = res.data;

        if (participants && participants.length > 0) {
          this.extraParticipants = participants.filter(person => {
            let notOnVisit = true;
            if (this.additionalVisitors && this.additionalVisitors.length > 0) {
              this.additionalVisitors.forEach(additionalVisitorOnVisit => {
                if (additionalVisitorOnVisit.additional_visitor_id === person.additional_visitor_id) {
                  notOnVisit = false;
                }
              });
            }
            return notOnVisit;
          }).map(additionalVisitor => ({
              fullName: `${additionalVisitor.firstname} ${additionalVisitor.lastname}`,
              birthDateAndAge: `${moment(additionalVisitor.dob).format('DD/MM/YYYY')} (${moment(new Date()).diff(additionalVisitor.dob, 'years')})`,
              under18: parseInt(moment(new Date()).diff(additionalVisitor.dob, 'years'), 10) < 18,
              ...additionalVisitor,
            }));
        }
      }).catch(() => {
        this.$toast.error('Error getting visitors additional visitors. Please check your connection and refresh the page', {
          toastClassName: ['toast-std', 'warning-toast'],
        });
      }).finally(() => {
        this.loading = false;
      })
    },
    addParticipant(participant) {
      InCellVisitsService.addAdditionalVisitor(this.visit._id, participant.additional_visitor_id).then(() => {
        this.$toast.success(`${participant.firstname} added to visit`, {
          toastClassName: ['toast-std', 'success-toast'],
        });
      }).catch(err => {
        const error = err && err.response && err.response.data && err.response.data.message;
        let msg = 'Could not add participant to visit, please refresh and try again';

        if (error) {
          msg = error.message;
        }

        this.$toast.error(msg, {
          toastClassName: ['toast-std', 'warning-toast'],
        });
      }).finally(() => {
        this.loading = false;
        this.closeAllModalAndRefresh();
      })
    },
    getNote(participant) {
      if (participant.verication_status !== 'Verified') {
        return 'Warning: The participant hasn\'t passed verification';
      }

      if (participant.under18 && !participant.safeguarded_at_prison) {
        return 'Warning: The participant is under 18 and has not completed the safeguarding checks!';
      }

      return '';
    },
    getVerificationStatus(status) {
      return status === 'AwaitingVerification' ? 'Awaiting Verification' : status;
    },
    viewParticipantDetails(item) {
      this.$emit('view', item);
    },
    rowClass(item) {
      if (item && item.verification_status !== 'Verified') {
        return 'table-danger';
      }
      return '';
    },
  },
};
</script>

<template>
  <section class="participants-add">
    <h5 class="mb-2">Choose Participants to Add</h5>

    <section v-if="!loading">
      <section v-if="extraParticipants.length">
        <b-table
          class="data-table"
          hover
          :items="extraParticipants"
          :fields="headers"
          :current-page="currentPage"
          :responsive="true"
          :tbody-tr-class="rowClass"
          @row-clicked="viewParticipantDetails"
        >
          <template #cell(birthDateAndAge)="data">
            <span class="d-flex flex-column" style="width: 115px">{{ data.item.birthDateAndAge }}</span>
            <span v-if="data.item.under18 && !data.item.safeguarded_at_prison" class="badge badge-danger badge-pill">UNDER 18</span>
            <span v-else-if="data.item.under18 && data.item.safeguarded_at_prison"
                  class="badge badge-success badge-pill"
            >UNDER 18</span>
          </template>

          <template #cell(status)="data">
            {{ getVerificationStatus(data.item.verification_status) }}
          </template>

          <template #cell(add)="data">
            <b-btn size="sm" variant="success" @click="showModal('modal-add-participant', true, data.item)"><span
              class="fa fa-plus"
            /></b-btn>
          </template>
        </b-table>
      </section>

      <p v-else>Visitor has no Additional Participants left to add.</p>
    </section>

    <b-spinner v-else variant="primary" label="Loading..." />

    <b-modal id="modal-add-participant" hide-footer>
      <warning-modal v-if="activeParticipant" key="add-warning"
                     title="Are you sure you want to add participant"
                     :subtitle="activeParticipant.firstname + ' ' + activeParticipant.lastname + '?'"
                     :note="getNote(activeParticipant)" :call="activeParticipant" @close="closeAllModalAndRefresh()"
                     @ok="addParticipant(activeParticipant)"
      />
    </b-modal>
  </section>
</template>

<style scoped>
.table-danger {
  background-color: #f8d7da !important;
}
</style>
